import { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { invoicesAPI, settingsAPI } from '@/lib/api';
import {
  formatDate,
  getPaymentStatusColor,
  generateInvoiceMessage,
  setCurrencySymbol,
} from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Printer, MessageCircle, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

export default function InvoiceDetailPage() {
  const { id } = useParams();
  const [invoice, setInvoice] = useState(null);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [sharing, setSharing] = useState(false);
  const invoiceRef = useRef(null);

  const formatCurrency = (amount) => {
    const sym = settings?.currency_symbol || 'Rs';
    const num = parseFloat(amount) || 0;

    return `${sym} ${num.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [invoiceRes, settingsRes] = await Promise.all([
          invoicesAPI.getById(id),
          settingsAPI.get(),
        ]);

        setInvoice(invoiceRes.data);
        setSettings(settingsRes.data || {});
        setCurrencySymbol(settingsRes.data?.currency_symbol || 'Rs');
      } catch (error) {
        console.error('Failed to load invoice:', error);
        toast.error('Failed to load invoice');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchData();
    }
  }, [id]);

  const handlePrint = () => {
    window.print();
  };

  const normalizePhoneForWhatsApp = (phone, defaultCountryCode = '94') => {
    if (!phone) return '';

    let cleaned = String(phone).trim();
    cleaned = cleaned.replace(/[^\d+]/g, '');

    if (cleaned.startsWith('00')) {
      cleaned = cleaned.slice(2);
    }

    if (cleaned.startsWith('+')) {
      cleaned = cleaned.slice(1);
    }

    if (cleaned.startsWith('0')) {
      cleaned = `${defaultCountryCode}${cleaned.slice(1)}`;
    }

    cleaned = cleaned.replace(/\D/g, '');
    return cleaned;
  };

  const buildWhatsAppLink = (phone, message) => {
    const defaultCountryCode = String('94').replace('+', '');
    const normalizedPhone = normalizePhoneForWhatsApp(phone, defaultCountryCode);

    if (!normalizedPhone) {
      return '';
    }

    const encodedMessage = encodeURIComponent(message || '');
    return `https://wa.me/${normalizedPhone}?text=${encodedMessage}`;
  };

  const generateInvoicePdfBlob = async () => {
    if (!invoiceRef.current) {
      throw new Error('Invoice content not found');
    }

    const element = invoiceRef.current;

    const canvas = await html2canvas(element, {
      scale: 2,
      useCORS: true,
      backgroundColor: '#ffffff',
      logging: false,
    });

    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF('p', 'mm', 'a4');

    const pageWidth = 210;
    const pageHeight = 297;
    const margin = 8;
    const usableWidth = pageWidth - margin * 2;

    const imgWidth = usableWidth;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    let heightLeft = imgHeight;
    let position = margin;

    pdf.addImage(imgData, 'PNG', margin, position, imgWidth, imgHeight);
    heightLeft -= pageHeight - margin * 2;

    while (heightLeft > 0) {
      pdf.addPage();
      position = margin - (imgHeight - heightLeft);
      pdf.addImage(imgData, 'PNG', margin, position, imgWidth, imgHeight);
      heightLeft -= pageHeight - margin * 2;
    }

    return pdf.output('blob');
  };

  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');

    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();

    URL.revokeObjectURL(url);
  };

  const handleWhatsApp = async () => {
    if (!invoice) {
      toast.error('Invoice data not loaded');
      return;
    }

    if (!invoice.customer_phone) {
      toast.error('Customer phone number is missing');
      return;
    }

    setSharing(true);

    try {
      const message = generateInvoiceMessage(invoice, settings);
      const pdfBlob = await generateInvoicePdfBlob();
      const fileName = `${invoice.invoice_no || 'invoice'}.pdf`;
      const pdfFile = new File([pdfBlob], fileName, {
        type: 'application/pdf',
      });

      const canUseFileShare =
        typeof navigator !== 'undefined' &&
        navigator.canShare &&
        navigator.canShare({ files: [pdfFile] });

      if (canUseFileShare && navigator.share) {
        await navigator.share({
          title: `Invoice ${invoice.invoice_no}`,
          text: message,
          files: [pdfFile],
        });

        toast.success('Invoice PDF shared');
        return;
      }

      downloadBlob(pdfBlob, fileName);

      const fallbackMessage = `${message}

Invoice PDF has been downloaded.
Please attach the downloaded PDF in WhatsApp and send it.`;

      const link = buildWhatsAppLink(invoice.customer_phone, fallbackMessage);

      if (!link) {
        toast.error('Invalid customer phone number');
        return;
      }

      window.open(link, '_blank', 'noopener,noreferrer');
      toast.success('PDF downloaded. Attach it in WhatsApp.');
    } catch (error) {
      console.error(error);
      toast.error('Failed to prepare WhatsApp PDF share');
    } finally {
      setSharing(false);
    }
  };

  const handlePaymentUpdate = async (status) => {
    try {
      await invoicesAPI.updatePayment(id, status);
      setInvoice((prev) => ({
        ...prev,
        payment_status: status,
      }));
      toast.success('Payment status updated');
    } catch (error) {
      console.error('Failed to update payment status:', error);
      toast.error('Failed to update payment status');
    }
  };

  const serviceLineDiscounts = (invoice?.service_lines || []).reduce(
    (sum, line) => sum + (parseFloat(line.discount) || 0),
    0,
  );

  const partLineDiscounts = (invoice?.part_lines || []).reduce(
    (sum, line) => sum + (parseFloat(line.discount) || 0),
    0,
  );

  const totalLineDiscounts = serviceLineDiscounts + partLineDiscounts;
  const totalAllDiscounts =
    totalLineDiscounts + (parseFloat(invoice?.discount_amount) || 0);

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="spinner" />
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="py-12 text-center">
        <p className="text-muted-foreground">Invoice not found</p>
        <Link to="/invoices">
          <Button variant="outline" className="mt-4">
            Back to Invoices
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="invoice-detail-page">
      <div className="no-print flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-4">
          <Link to="/invoices">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-5 w-5" />
            </Button>
          </Link>

          <div>
            <h1 className="font-heading text-3xl font-black tracking-tight">
              Invoice {invoice.invoice_no}
            </h1>
            <p className="text-muted-foreground">{formatDate(invoice.date)}</p>
          </div>
        </div>

        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={handleWhatsApp}
            data-testid="whatsapp-btn"
            disabled={sharing}
          >
            <MessageCircle className="mr-2 h-4 w-4" />
            {sharing ? 'Preparing PDF...' : 'WhatsApp'}
          </Button>

          <Button variant="outline" onClick={handlePrint} data-testid="print-btn">
            <Printer className="mr-2 h-4 w-4" />
            Print
          </Button>
        </div>
      </div>

      <div className="invoice-print bg-white" data-testid="invoice-content-card">
        <style>{`
          @media print {
            @page { size: A4; margin: 10mm; }
            body * { visibility: hidden; }
            .invoice-print, .invoice-print * { visibility: visible; }
            .invoice-print {
              position: absolute;
              left: 0;
              top: 0;
              width: 100%;
              font-size: 9px;
              color: #000 !important;
              background: #fff !important;
            }
            .no-print { display: none !important; }
            .invoice-table th, .invoice-table td { padding: 3px 5px !important; }
          }
        `}</style>

        <div
          ref={invoiceRef}
          className="mx-auto max-w-4xl bg-white p-4 text-xs md:p-5"
          style={{ fontFamily: 'Arial, sans-serif', color: '#000' }}
        >
          {settings.invoice_header_image ? (
            <div className="mb-3">
              <img
                src={settings.invoice_header_image}
                alt="Invoice Header"
                className="w-full object-contain"
                data-testid="print-header-image"
              />
            </div>
          ) : (
            <div
              className="mb-3 w-full"
              style={{ height: '3cm' }}
              data-testid="print-header-placeholder"
            />
          )}

          <div className="mb-3 flex justify-between border-y border-gray-300 py-2">
            <div className="flex-1">
              <div className="mb-1 text-[10px] font-bold">BILL TO</div>
              <div className="pl-2 text-[10px]">
                <div className="font-semibold">{invoice.customer_name}</div>

                {invoice.customer_address && (
                  <div className="text-gray-600">{invoice.customer_address}</div>
                )}

                <div className="text-gray-600">{invoice.customer_phone}</div>

                {invoice.customer_email && (
                  <div className="text-gray-600">{invoice.customer_email}</div>
                )}
              </div>
            </div>

            <div className="flex-1 px-4">
              <div className="mb-1 text-[10px] font-bold">VEHICLE INFORMATION</div>
              <table className="text-left text-[10px]">
                <tbody>
                  <tr>
                    <td className="pr-2 text-gray-600">Number</td>
                    <td className="font-mono font-semibold">
                      {invoice.vehicle_number}
                    </td>
                  </tr>

                  {invoice.vehicle_make && (
                    <tr>
                      <td className="pr-2 text-gray-600">Make/Model</td>
                      <td>
                        {invoice.vehicle_make} - {invoice.vehicle_model}
                      </td>
                    </tr>
                  )}

                  {invoice.vehicle_year && (
                    <tr>
                      <td className="pr-2 text-gray-600">Year</td>
                      <td>{invoice.vehicle_year}</td>
                    </tr>
                  )}

                  {invoice.vehicle_mileage && (
                    <tr>
                      <td className="pr-2 text-gray-600">Mileage</td>
                      <td>{Number(invoice.vehicle_mileage).toLocaleString()} km</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="text-right">
              <table className="ml-auto text-left text-[10px]">
                <tbody>
                  <tr>
                    <td className="py-0.5 pr-2 font-bold">Invoice NO.</td>
                    <td className="font-mono">{invoice.invoice_no}</td>
                  </tr>
                  <tr>
                    <td className="py-0.5 pr-2 font-bold">DATE</td>
                    <td>{invoice.date}</td>
                  </tr>
                  <tr>
                    <td className="py-0.5 pr-2 font-bold">CUSTOMER ID</td>
                    <td className="font-mono text-[9px]">
                      {invoice.customer_id?.slice(0, 8).toUpperCase()}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {invoice.service_lines?.length > 0 && (
            <div className="mb-3">
              <div className="mb-1 text-[10px] font-bold">SERVICES</div>

              <table className="invoice-table w-full border-collapse text-[10px]">
                <thead>
                  <tr className="bg-gray-200">
                    <th className="border border-gray-400 p-1.5 text-left font-bold">
                      DESCRIPTION
                    </th>
                    <th className="w-24 border border-gray-400 p-1.5 text-right font-bold">
                      AMOUNT
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {invoice.service_lines.map((line, idx) => (
                    <tr
                      key={idx}
                      className={idx % 2 === 1 ? 'bg-gray-50' : 'bg-white'}
                    >
                      <td className="border border-gray-400 p-1.5">
                        {line.description}
                      </td>
                      <td className="border border-gray-400 p-1.5 text-right font-mono font-semibold">
                        {formatCurrency(line.amount)}
                      </td>
                    </tr>
                  ))}
                </tbody>

                <tfoot>
                  <tr className="bg-gray-100 font-bold">
                    <td className="border border-gray-400 p-1.5 text-right">
                      SERVICE TOTAL
                    </td>
                    <td className="border border-gray-400 p-1.5 text-right font-mono">
                      {formatCurrency(invoice.service_total)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}

          {invoice.part_lines?.length > 0 && (
            <div className="mb-3">
              <div className="mb-1 text-[10px] font-bold">PARTS & PRODUCTS</div>

              <table className="invoice-table w-full border-collapse text-[10px]">
                <thead>
                  <tr className="bg-gray-200">
                    <th className="border border-gray-400 p-1.5 text-left font-bold">
                      ITEM
                    </th>
                    <th className="w-14 border border-gray-400 p-1.5 text-center font-bold">
                      QTY
                    </th>
                    <th className="w-20 border border-gray-400 p-1.5 text-right font-bold">
                      UNIT PRICE
                    </th>
                    <th className="w-20 border border-gray-400 p-1.5 text-right font-bold">
                      DISCOUNT
                    </th>
                    <th className="w-20 border border-gray-400 p-1.5 text-right font-bold">
                      TOTAL
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {invoice.part_lines.map((line, idx) => {
                    const unitPrice = parseFloat(line.unit_price) || 0;
                    const qty = parseInt(line.qty, 10) || 0;
                    const discount = parseFloat(line.discount) || 0;
                    const lineTotal = unitPrice * qty - discount;

                    return (
                      <tr
                        key={idx}
                        className={idx % 2 === 1 ? 'bg-gray-50' : 'bg-white'}
                      >
                        <td className="border border-gray-400 p-1.5">
                          {line.description}
                        </td>
                        <td className="border border-gray-400 p-1.5 text-center font-mono">
                          {line.qty}
                        </td>
                        <td className="border border-gray-400 p-1.5 text-right font-mono">
                          {formatCurrency(unitPrice)}
                        </td>
                        <td className="border border-gray-400 p-1.5 text-right font-mono text-green-600">
                          {discount > 0 ? formatCurrency(discount) : '-'}
                        </td>
                        <td className="border border-gray-400 p-1.5 text-right font-mono font-semibold">
                          {formatCurrency(lineTotal)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>

                <tfoot>
                  <tr className="bg-gray-100 font-bold">
                    <td colSpan="2" className="border border-gray-400 p-1.5 text-right">
                      PARTS TOTAL
                    </td>
                    <td className="border border-gray-400 p-1.5 text-right font-mono">
                      {formatCurrency(
                        invoice.part_lines.reduce((sum, line) => {
                          const unitPrice = parseFloat(line.unit_price) || 0;
                          const qty = parseInt(line.qty, 10) || 0;
                          return sum + unitPrice * qty;
                        }, 0),
                      )}
                    </td>
                    <td className="border border-gray-400 p-1.5 text-right font-mono text-green-600">
                      {partLineDiscounts > 0 ? formatCurrency(partLineDiscounts) : '-'}
                    </td>
                    <td className="border border-gray-400 p-1.5 text-right font-mono">
                      {formatCurrency(invoice.parts_total)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}

          <div className="flex items-start justify-between border-t-2 border-gray-500 pt-2">
            <div className="max-w-[280px] text-[10px] text-gray-600">
              <div className="mb-2">
                <span className="font-bold">Payment Method:</span>{' '}
                {invoice.payment_method?.toUpperCase()}
              </div>

              Make all checks payable to
              <br />

              <span className="font-bold">
                {settings.business_name || 'BUSINESS NAME'}
              </span>

            </div>

            <table className="text-[10px]">
              <tbody>
                <tr>
                  <td className="py-0.5 pr-4 text-right">Services Total</td>
                  <td className="w-24 text-right font-mono">
                    {formatCurrency(invoice.service_total || 0)}
                  </td>
                </tr>

                <tr>
                  <td className="py-0.5 pr-4 text-right">Parts Total</td>
                  <td className="w-24 text-right font-mono">
                    {formatCurrency(invoice.parts_total || 0)}
                  </td>
                </tr>

                <tr>
                  <td className="py-0.5 pr-4 text-right font-semibold">Subtotal</td>
                  <td className="w-24 text-right font-mono font-semibold">
                    {formatCurrency(invoice.subtotal || 0)}
                  </td>
                </tr>

                {totalLineDiscounts > 0 && (
                  <tr className="text-green-600">
                    <td className="py-0.5 pr-4 text-right">Line Discounts</td>
                    <td className="w-24 text-right font-mono">
                      -{formatCurrency(totalLineDiscounts)}
                    </td>
                  </tr>
                )}

                {Number(invoice.discount_amount) > 0 && (
                  <tr className="text-green-600">
                    <td className="py-0.5 pr-4 text-right">Invoice Discount</td>
                    <td className="w-24 text-right font-mono">
                      -{formatCurrency(invoice.discount_amount)}
                    </td>
                  </tr>
                )}

                {totalAllDiscounts > 0 && (
                  <tr className="font-semibold text-green-600">
                    <td className="py-0.5 pr-4 text-right">TOTAL DISCOUNT</td>
                    <td className="w-24 text-right font-mono">
                      -{formatCurrency(totalAllDiscounts)}
                    </td>
                  </tr>
                )}

                {Number(invoice.tax_amount) > 0 && (
                  <tr>
                    <td className="py-0.5 pr-4 text-right">
                      Tax ({invoice.tax_rate}%)
                    </td>
                    <td className="w-24 text-right font-mono">
                      {formatCurrency(invoice.tax_amount)}
                    </td>
                  </tr>
                )}

                <tr className="border-t-2 border-gray-500">
                  <td className="pt-2 pr-4 text-right text-sm font-bold">
                    GRAND TOTAL
                  </td>
                  <td className="w-24 pt-2 text-right font-mono text-sm font-bold">
                    {formatCurrency(invoice.grand_total || 0)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="mt-4 text-center">
            <Badge
              className={`${getPaymentStatusColor(
                invoice.payment_status,
              )} px-3 py-0.5 text-[10px]`}
            >
              {invoice.payment_status?.toUpperCase()}
            </Badge>

            <div className="mt-8 pt-4">
              <h3 className="text-lg font-bold text-gray-800">
                Thank you for choosing our company
              </h3>

              <p className="mt-1 text-xs text-gray-600">
                We appreciate your business and look forward to serving you again.
              </p>
            </div>
          </div>

          {invoice.notes && (
            <div className="mt-2 rounded border border-gray-200 bg-gray-50 p-2 text-[10px]">
              <span className="font-semibold">Notes: </span>
              {invoice.notes}
            </div>
          )}
        </div>
      </div>

      <Card className="no-print">
        <CardHeader>
          <CardTitle className="font-heading">Update Payment Status</CardTitle>
        </CardHeader>

        <CardContent>
          <div className="flex flex-wrap items-center gap-4">
            <select
              value={invoice.payment_status || 'pending'}
              onChange={(e) => handlePaymentUpdate(e.target.value)}
              className="h-10 w-48 rounded-md border border-input bg-background px-3 py-2 text-sm"
              data-testid="update-payment-status-select"
            >
              <option value="paid">Paid</option>
              <option value="pending">Pending</option>
              <option value="partial">Partial</option>
            </select>

            {invoice.payment_status === 'paid' && (
              <div className="flex items-center gap-2 text-green-600">
                <CheckCircle className="h-5 w-5" />
                <span>Payment Complete</span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Card className="no-print">
        <CardHeader>
          <CardTitle className="font-heading">Profit Analysis</CardTitle>
        </CardHeader>

        <CardContent>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            <div>
              <p className="text-sm text-muted-foreground">Revenue</p>
              <p className="font-mono text-2xl font-bold">
                {formatCurrency(invoice.grand_total)}
              </p>
            </div>

            <div>
              <p className="text-sm text-muted-foreground">Cost</p>
              <p className="font-mono text-2xl font-bold text-red-600">
                {formatCurrency(invoice.cost_total)}
              </p>
            </div>

            <div>
              <p className="text-sm text-muted-foreground">Profit</p>
              <p
                className="font-mono text-2xl font-bold text-green-600"
                data-testid="invoice-profit"
              >
                {formatCurrency(invoice.profit)}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}